/** Add / re-auth account form modal (VistoeraTASK-00558).
 *
 * Switching other accounts happens inline in `TenantAccountMenu` — this
 * modal is only the credential form. No account list, no "Back" control. */
import { Button, Form, Input, Modal } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addDeviceAccount, type DeviceAccountListResponse, type SaasApiError } from '../api'
import './account-switch-modal.css'

export type AccountCredentialModalMode = 'ADD_ACCOUNT_LOGIN' | 'REAUTH_REQUIRED'

interface Props {
  open: boolean
  mode: AccountCredentialModalMode
  /** Prefill + lock email when re-authenticating a bound account. */
  email?: string
  onClose: () => void
  onSuccess: (data: DeviceAccountListResponse) => void
}

export default function AccountSwitchModal({ open, mode, email = '', onClose, onSuccess }: Props) {
  const { t } = useTranslation('accountEntry')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form] = Form.useForm<{ email: string; password: string }>()

  const resetForm = useCallback(() => {
    setError(null)
    form.resetFields()
    if (mode === 'REAUTH_REQUIRED' && email) {
      form.setFieldsValue({ email, password: '' })
    }
  }, [form, mode, email])

  useEffect(() => {
    if (!open) {
      setError(null)
      form.resetFields()
      return
    }
    resetForm()
  }, [open, resetForm, form])

  const onSubmit = async (values: { email: string; password: string }) => {
    setSubmitting(true)
    setError(null)
    try {
      const { data } = await addDeviceAccount(values.email.trim(), values.password)
      form.resetFields()
      onSuccess(data)
    } catch (err) {
      const apiErr = err as SaasApiError
      if (apiErr.code === 'invalid_credentials' || apiErr.status === 401) {
        setError(t('switchAccount.addLoginInvalid'))
      } else if (apiErr.code === 'email_not_verified') {
        setError(t('switchAccount.addLoginUnverified'))
      } else if (apiErr.code === 'already_bound' || apiErr.status === 409) {
        setError(t('switchAccount.addLoginDuplicate'))
      } else {
        setError(apiErr.message || t('switchAccount.addLoginFailed'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={mode === 'REAUTH_REQUIRED' ? t('switchAccount.reauthTitle') : t('switchAccount.addTitle')}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      className="account-switch-modal"
      maskClosable={!submitting}
      closable={!submitting}
    >
      <div className="account-switch-add-form">
        <Form form={form} layout="vertical" onFinish={(v) => void onSubmit(v)} disabled={submitting}>
          <Form.Item
            name="email"
            label={t('switchAccount.emailLabel')}
            rules={[{ required: true, message: t('switchAccount.emailRequired') }]}
          >
            <Input autoComplete="username" disabled={mode === 'REAUTH_REQUIRED'} />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('switchAccount.passwordLabel')}
            rules={[{ required: true, message: t('switchAccount.passwordRequired') }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          {error ? <div className="account-switch-add-error">{error}</div> : null}
          <Button type="primary" htmlType="submit" block loading={submitting}>
            {mode === 'REAUTH_REQUIRED' ? t('switchAccount.reauthSubmit') : t('switchAccount.addSubmit')}
          </Button>
        </Form>
      </div>
    </Modal>
  )
}
